import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  TextField,
  Typography,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import PlaceIcon from '@mui/icons-material/Place';
import EmailIcon from '@mui/icons-material/Email';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import ErrorIcon from '@mui/icons-material/Error';
import { useNavigate } from 'react-router-dom';
import { useBrand } from '../../contexts/BrandContext';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../services/api';
import CadenceEditor from '../brand/CadenceEditor';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import type {
  Brand,
  BrandStrategy,
  EmailLanguage,
  EmailRecipient,
  Envergure,
  LocationScope,
} from '@traaker/shared';

const BRAND_STRATEGY_LIMITS = {
  industry: 80,
  clientPersonas: 500,
  geographicFocus: 200,
  description: 800,
  aliases: { maxItems: 10, maxCharsPerItem: 80 },
  differentiators: { maxItems: 10, maxCharsPerItem: 150 },
  scenarios: { maxItems: 15, maxCharsPerItem: 200 },
  keywords: { maxItems: 30, maxCharsPerItem: 60 },
  customerQuestions: { maxItems: 20, maxCharsPerItem: 200 },
  customerObjections: { maxItems: 20, maxCharsPerItem: 200 },
  avoidTopics: { maxItems: 20, maxCharsPerItem: 150 },
  targetLanguages: { maxItems: 10, maxCharsPerItem: 10 },
  existingAssets: {
    wikipediaUrl: 500,
    pressKitUrl: 500,
    notableMentions: { maxItems: 20, maxCharsPerItem: 300 },
  },
} as const;

const EMAIL_LANGUAGES: { code: EmailLanguage; label: string }[] = [
  { code: 'fr', label: 'Français' },
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
  { code: 'de', label: 'Deutsch' },
  { code: 'it', label: 'Italiano' },
];
const DEFAULT_EMAIL_LANGUAGE: EmailLanguage = 'fr';

const SCOPE_LABELS: Record<LocationScope, string> = {
  worldwide: 'Mondial',
  national: 'National',
  city: 'Ville',
  address: 'Adresse',
};

const BRAND_LIMITS: Record<string, number> = { lite: 1, pro: 3, enterprise: -1 };
const ZONE_LIMITS: Record<string, number> = { lite: 1, pro: 5, enterprise: -1 };

export default function BrandsSettings() {
  const { brands, refreshBrands, setActiveBrand } = useBrand();
  const { tenant, user } = useAuth();
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState<string | null>(null);
  const [brandDialog, setBrandDialog] = useState<{ open: boolean; brand?: Brand }>({ open: false });
  const [message, setMessage] = useState<string | null>(null);
  const [discovery, setDiscovery] = useState<{ open: boolean; brandId?: string; brandName?: string }>({ open: false });
  const [emailDialog, setEmailDialog] = useState<{ open: boolean; brandId?: string; recipients: EmailRecipient[] }>({ open: false, recipients: [] });
  const [emailRecipients, setEmailRecipients] = useState<Record<string, EmailRecipient[]>>({});

  const isAdmin = user?.role === 'owner' || user?.role === 'admin';
  const tier = tenant?.tier || 'lite';
  const maxBrands = tenant?.settings?.quotaOverrides?.brands ?? (BRAND_LIMITS[tier] ?? 1);
  const maxZones = tenant?.settings?.quotaOverrides?.zones ?? (ZONE_LIMITS[tier] ?? 1);
  const canAddBrand = maxBrands === -1 || brands.length < maxBrands;
  const totalLocations = brands.reduce((sum, b) => sum + b.locations.length, 0);

  useEffect(() => {
    if (brands.length === 0) return;
    const normalize = (raw: unknown): EmailRecipient[] => {
      if (!Array.isArray(raw)) return [];
      return raw.map((r: any) => typeof r === 'string'
        ? { email: r, language: DEFAULT_EMAIL_LANGUAGE, frequency: 'weekly' as const }
        : {
            email: r.email,
            language: (r.language as EmailLanguage) || DEFAULT_EMAIL_LANGUAGE,
            frequency: (['daily', 'weekly', 'monthly'].includes(r.frequency)
              ? r.frequency
              : 'weekly') as 'daily' | 'weekly' | 'monthly',
          },
      );
    };
    const fetchRecipients = async () => {
      const map: Record<string, EmailRecipient[]> = {};
      for (const brand of brands) {
        try {
          const cfg = await api.get<{ recipients?: unknown }>(`/brands/${brand.brandId}/email-config`);
          map[brand.brandId] = normalize(cfg.recipients);
        } catch {
          map[brand.brandId] = [];
        }
      }
      setEmailRecipients(map);
    };
    fetchRecipients();
  }, [brands]);

  const handleSaveRecipients = async (brandId: string, recipients: EmailRecipient[]) => {
    try {
      await api.patch(`/brands/${brandId}/email-recipients`, { recipients });
      setEmailRecipients((prev) => ({ ...prev, [brandId]: recipients }));
      setMessage('Destinataires mis a jour');
    } catch {
      setMessage('Erreur lors de la mise a jour des destinataires');
    }
  };

  const handleDeleteBrand = async (brandId: string) => {
    try {
      await api.delete(`/brands/${brandId}`);
      await refreshBrands();
    } catch {
      setMessage('Erreur lors de la suppression');
    }
  };

  const goManageLocations = (brand: Brand) => {
    setActiveBrand(brand);
    navigate('/strategy');
  };

  return (
    <Box>
      {message && (
        <Alert severity="info" onClose={() => setMessage(null)} sx={{ mb: 2 }}>
          {message}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h6" fontWeight={600}>
            Marques
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {brands.length}{maxBrands !== -1 ? ` / ${maxBrands}` : ''} marque{brands.length > 1 ? 's' : ''}
            {' · '}
            {totalLocations}{maxZones !== -1 ? ` / ${maxZones}` : ''} zone{totalLocations > 1 ? 's' : ''}
          </Typography>
        </Box>
        {isAdmin && (
          <Button
            startIcon={<AddIcon />}
            variant="outlined"
            size="small"
            disabled={!canAddBrand}
            onClick={() => setBrandDialog({ open: true })}
          >
            Ajouter une marque
          </Button>
        )}
      </Box>

      {brands.map((brand) => (
        <Card key={brand.brandId} sx={{ mb: 1.5 }}>
          <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton
                size="small"
                onClick={() => setExpanded(expanded === brand.brandId ? null : brand.brandId)}
              >
                {expanded === brand.brandId ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography variant="body1" fontWeight={600} noWrap>
                  {brand.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" noWrap>
                  {brand.url || 'Aucun site web'}
                </Typography>
              </Box>
              <Chip
                icon={<PlaceIcon sx={{ fontSize: '14px !important' }} />}
                label={`${brand.locations.length} zone${brand.locations.length > 1 ? 's' : ''}`}
                size="small"
                variant="outlined"
              />
              {isAdmin && (
                <>
                  <IconButton size="small" onClick={() => setBrandDialog({ open: true, brand })}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDeleteBrand(brand.brandId)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </>
              )}
            </Box>

            <Collapse in={expanded === brand.brandId}>
              <Box sx={{ mt: 1.5, pl: 5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="caption" fontWeight={600} color="text.secondary" textTransform="uppercase">
                    Zones geographiques
                  </Typography>
                  {isAdmin && (
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => goManageLocations(brand)}
                      sx={{ textTransform: 'none' }}
                    >
                      Gérer →
                    </Button>
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 0.75, alignItems: 'center' }}>
                  {brand.locations.map((loc) => (
                    <Chip
                      key={loc.locationId}
                      label={`${loc.label}${loc.scope ? ` (${SCOPE_LABELS[loc.scope]})` : ''}`}
                      size="small"
                      variant="outlined"
                    />
                  ))}
                  {brand.locations.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                      Aucune zone configurée — {isAdmin ? 'ajoutez-en depuis Brand Knowledge.' : 'demandez à un administrateur.'}
                    </Typography>
                  )}
                </Box>

                {brand.competitors.length > 0 && (
                  <Box sx={{ mt: 1.5 }}>
                    <Typography variant="caption" fontWeight={600} color="text.secondary" textTransform="uppercase">
                      Concurrents
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 0.75 }}>
                      {brand.competitors.map((c) => (
                        <Chip key={c} label={c} size="small" variant="outlined" />
                      ))}
                    </Box>
                  </Box>
                )}

                <Box sx={{ mt: 1.5 }}>
                  <Typography variant="caption" fontWeight={600} color="text.secondary" textTransform="uppercase">
                    <EmailIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                    Rapport par email
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 0.75, alignItems: 'center' }}>
                    {(emailRecipients[brand.brandId] || []).map((r) => (
                      <Chip
                        key={r.email}
                        label={`${r.email} · ${r.language.toUpperCase()}`}
                        size="small"
                        onDelete={isAdmin ? () => {
                          const updated = (emailRecipients[brand.brandId] || []).filter((x) => x.email !== r.email);
                          handleSaveRecipients(brand.brandId, updated);
                        } : undefined}
                      />
                    ))}
                    {(emailRecipients[brand.brandId] || []).length === 0 && (
                      <Typography variant="body2" color="text.secondary">
                        Aucun destinataire configure
                      </Typography>
                    )}
                    {isAdmin && (
                      <Chip
                        icon={<AddIcon sx={{ fontSize: '14px !important' }} />}
                        label="Ajouter"
                        size="small"
                        variant="outlined"
                        onClick={() => setEmailDialog({
                          open: true,
                          brandId: brand.brandId,
                          recipients: emailRecipients[brand.brandId] || [],
                        })}
                        sx={{ cursor: 'pointer' }}
                      />
                    )}
                  </Box>
                </Box>
              </Box>
            </Collapse>
          </CardContent>
        </Card>
      ))}

      {brands.length === 0 && (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">
              Aucune marque configuree. Ajoutez votre premiere marque pour commencer.
            </Typography>
          </CardContent>
        </Card>
      )}

      <BrandDialog
        {...brandDialog}
        tier={tier}
        onClose={() => setBrandDialog({ open: false })}
        onSaved={refreshBrands}
        onCreated={(brandId, brandName) => setDiscovery({ open: true, brandId, brandName })}
      />
      <DiscoveryDialog
        {...discovery}
        onSaved={refreshBrands}
        onClose={() => {
          setDiscovery({ open: false });
          refreshBrands();
        }}
      />
      <EmailRecipientsDialog
        open={emailDialog.open}
        brandId={emailDialog.brandId}
        recipients={emailDialog.recipients}
        onClose={() => setEmailDialog({ open: false, recipients: [] })}
        onSaved={(brandId, recipients) => handleSaveRecipients(brandId, recipients)}
      />
    </Box>
  );
}

function BrandDialog({
  open,
  brand,
  tier,
  onClose,
  onSaved,
  onCreated,
}: {
  open: boolean;
  brand?: Brand;
  tier: string;
  onClose: () => void;
  onSaved: () => Promise<void>;
  onCreated?: (brandId: string, brandName: string) => void;
}) {
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [competitors, setCompetitors] = useState('');
  const [includeUrls, setIncludeUrls] = useState('');
  const [excludeUrls, setExcludeUrls] = useState('');
  const [emWatchInclude, setEmWatchInclude] = useState('');
  const [emWatchExclude, setEmWatchExclude] = useState('');
  const [showAuditConfig, setShowAuditConfig] = useState(false);
  const [showEarnedMediaWatch, setShowEarnedMediaWatch] = useState(false);
  const [showCadence, setShowCadence] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setName(brand?.name || '');
      setUrl(brand?.url || '');
      setCompetitors(brand?.competitors.join(', ') || '');
      setIncludeUrls((brand?.auditConfig?.includeUrls || []).join('\n'));
      setExcludeUrls((brand?.auditConfig?.excludeUrls || []).join('\n'));
      setEmWatchInclude(
        (brand?.auditConfig?.earnedMediaWatch?.include || []).join('\n'),
      );
      setEmWatchExclude(
        (brand?.auditConfig?.earnedMediaWatch?.exclude || []).join('\n'),
      );
      setShowAuditConfig(
        (brand?.auditConfig?.includeUrls?.length || 0) > 0 ||
        (brand?.auditConfig?.excludeUrls?.length || 0) > 0,
      );
      setShowEarnedMediaWatch(
        (brand?.auditConfig?.earnedMediaWatch?.include?.length || 0) > 0 ||
        (brand?.auditConfig?.earnedMediaWatch?.exclude?.length || 0) > 0,
      );
      setError(null);
    }
  }, [open, brand]);

  const handleSubmit = async () => {
    setError(null);
    const parseLines = (s: string) =>
      s.split('\n').map((l) => l.trim()).filter(Boolean);
    const includes = parseLines(includeUrls);
    const excludes = parseLines(excludeUrls);
    const watchInclude = parseLines(emWatchInclude);
    const watchExclude = parseLines(emWatchExclude);
    const body: Record<string, unknown> = {
      name,
      url: url || undefined,
      competitors: competitors ? competitors.split(',').map((c) => c.trim()).filter(Boolean) : [],
    };
    try {
      if (brand) {
        // Update name / url / competitors via PATCH /brands/:id.
        // Audit config fields go through PATCH /brands/:id/audit-config so
        // they merge with cadenceOverride + schedules instead of replacing
        // the full auditConfig.
        await api.patch(`/brands/${brand.brandId}`, body);
        await api.patch(`/brands/${brand.brandId}/audit-config`, {
          includeUrls: includes,
          excludeUrls: excludes,
          earnedMediaWatch: {
            include: watchInclude,
            exclude: watchExclude,
          },
        });
        await onSaved();
        onClose();
      } else {
        const created = await api.post<Brand>('/brands', body);
        await onSaved();
        onClose();
        if (url && onCreated) {
          onCreated(created.brandId, created.name);
        }
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{brand ? 'Modifier la marque' : 'Ajouter une marque'}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
        {error && <Alert severity="error">{error}</Alert>}
        <TextField label="Nom de la marque" value={name} onChange={(e) => setName(e.target.value)} fullWidth required />
        <TextField label="Site web" value={url} onChange={(e) => setUrl(e.target.value)} fullWidth placeholder="https://example.com" required />
        <TextField
          label="Concurrents"
          value={competitors}
          onChange={(e) => setCompetitors(e.target.value)}
          fullWidth
          placeholder="concurrent1.com, concurrent2.com"
          helperText="Separes par des virgules"
        />

        {brand && (
          <>
            <Box
              onClick={() => setShowAuditConfig(!showAuditConfig)}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1,
                cursor: 'pointer', userSelect: 'none',
                mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider',
              }}
            >
              {showAuditConfig ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              <Typography variant="subtitle2" fontWeight={600}>
                Selection avancee des pages (Audit Website)
              </Typography>
            </Box>
            <Collapse in={showAuditConfig}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Optionnel. Par defaut, l'audit choisit automatiquement les pages pertinentes
                  (exclut utilitaires, doublons de langue, pages sans valeur GEO). Utilisez ces
                  listes pour forcer l'inclusion ou l'exclusion de certaines pages. Glob supporte :
                  {' '}<code>*</code> (segment), <code>**</code> (plusieurs segments), <code>?</code>
                  {' '}(un caractere). Ex : <code>**/blog/draft-*</code>.
                </Typography>
                <TextField
                  label="URLs a toujours inclure"
                  value={includeUrls}
                  onChange={(e) => setIncludeUrls(e.target.value)}
                  fullWidth
                  multiline
                  minRows={2}
                  maxRows={6}
                  placeholder="https://example.com/page-importante&#10;**/category/featured/*"
                  helperText="Un pattern par ligne. Ces URLs contournent les filtres automatiques et le budget de pages."
                />
                <TextField
                  label="URLs a toujours exclure"
                  value={excludeUrls}
                  onChange={(e) => setExcludeUrls(e.target.value)}
                  fullWidth
                  multiline
                  minRows={2}
                  maxRows={6}
                  placeholder="**/drafts/*&#10;**/internal-*"
                  helperText="Un pattern par ligne. Ces URLs sont exclues avant tout autre traitement."
                />
              </Box>
            </Collapse>

            <Box
              onClick={() => setShowEarnedMediaWatch(!showEarnedMediaWatch)}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1,
                cursor: 'pointer', userSelect: 'none',
                mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider',
              }}
            >
              {showEarnedMediaWatch ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              <Typography variant="subtitle2" fontWeight={600}>
                Surveillance Earned Media (domaines)
              </Typography>
            </Box>
            <Collapse in={showEarnedMediaWatch}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Optionnel. Domaines à surveiller en priorité ou à exclure de la
                  recherche Earned Media. Indiquez des <strong>domaines racines</strong>
                  (sans <code>https://</code> ni chemin), un par ligne.
                </Typography>
                <TextField
                  label="Domaines à toujours surveiller"
                  value={emWatchInclude}
                  onChange={(e) => setEmWatchInclude(e.target.value)}
                  fullWidth
                  multiline
                  minRows={2}
                  maxRows={6}
                  placeholder="silicon.fr&#10;latribune.fr&#10;lesnumeriques.com"
                  helperText="Un domaine par ligne. L'orchestrateur lance une recherche dédiée par domaine en plus des angles standards."
                />
                <TextField
                  label="Domaines à exclure (faux positifs / homonymes)"
                  value={emWatchExclude}
                  onChange={(e) => setEmWatchExclude(e.target.value)}
                  fullWidth
                  multiline
                  minRows={2}
                  maxRows={6}
                  placeholder="acme-toolworks.com&#10;acme.health"
                  helperText="Un domaine par ligne. Ignorés pour cette marque uniquement."
                />
              </Box>
            </Collapse>

            <Box
              onClick={() => setShowCadence(!showCadence)}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1,
                cursor: 'pointer', userSelect: 'none',
                mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider',
              }}
            >
              {showCadence ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              <Typography variant="subtitle2" fontWeight={600}>
                Cadence des audits
              </Typography>
            </Box>
            <Collapse in={showCadence}>
              <Box sx={{ pt: 1 }}>
                <CadenceEditor
                  brandId={brand.brandId}
                  tier={tier}
                  cadenceOverride={brand.auditConfig?.cadenceOverride}
                  onSaved={onSaved}
                />
              </Box>
            </Collapse>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annuler</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={!name || !url}>
          {brand ? 'Enregistrer' : 'Ajouter'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Brand Discovery Dialog ──

const DISCOVERY_STEPS = [
  'Analyse de la page d\'accueil',
  'Selection des pages cles',
  'Analyse des pages',
  'Generation de la strategie',
];

const ENVERGURE_LABELS: Record<Envergure, string> = {
  'hyper-local': 'Hyper-locale (1 ville / quartier)',
  'multi-local': 'Multi-locale (plusieurs villes)',
  'mono-country': 'Mono-pays (nationale)',
  'multi-country': 'Multi-pays',
  global: 'Mondiale',
};

type SectionKey =
  | 'identity'
  | 'market'
  | 'locations'
  | 'valueProp'
  | 'scenarios'
  | 'keywords'
  | 'customerQuestions'
  | 'customerObjections'
  | 'avoidTopics'
  | 'languagesMaturity'
  | 'existingAssets';

interface SectionDef {
  key: SectionKey;
  title: string;
  description: string;
  usedBy: string;
  isEmpty: (s: BrandStrategy) => boolean;
}

const SECTIONS: SectionDef[] = [
  {
    key: 'identity',
    title: 'Identité',
    description: 'Secteur d\'activité et noms alternatifs de la marque.',
    usedBy: 'Earned Media, Brand Perception, Veille',
    isEmpty: (s) => !s.industry && !(s.aliases && s.aliases.length > 0),
  },
  {
    key: 'market',
    title: 'Marché cible',
    description: 'Qui sont vos clients et où les servez-vous ?',
    usedBy: 'Brand Perception (génération de requêtes)',
    isEmpty: (s) => !s.clientPersonas && !s.businessModel && !s.geographicFocus,
  },
  {
    key: 'locations',
    title: 'Localisations',
    description:
      'Zones géographiques concrètes que la marque veut couvrir (villes, pays, monde). Les audits en tiennent compte pour contextualiser les requêtes.',
    usedBy: 'Tous les audits (contexte géographique)',
    // Always considered "empty" from strategy POV — locations live on the brand object, not on strategy.
    isEmpty: () => true,
  },
  {
    key: 'valueProp',
    title: 'Proposition de valeur',
    description: 'Ce que fait votre marque et ce qui la distingue.',
    usedBy: 'Earned Media, Brand Perception, Veille',
    isEmpty: (s) => !s.description && !(s.differentiators && s.differentiators.length > 0),
  },
  {
    key: 'scenarios',
    title: 'Cas d\'usage / Sales Plays',
    description: 'Situations concrètes où un client a besoin de vos services.',
    usedBy: 'Brand Perception, Veille',
    isEmpty: (s) => !(s.scenarios && s.scenarios.length > 0),
  },
  {
    key: 'keywords',
    title: 'Sujets d\'autorité',
    description: 'Mots-clés et thèmes sur lesquels la marque veut être reconnue.',
    usedBy: 'Earned Media, Brand Perception, Veille',
    isEmpty: (s) => !(s.keywords && s.keywords.length > 0),
  },
  {
    key: 'customerQuestions',
    title: 'Questions avant achat',
    description:
      'Questions neutres que vos prospects posent à un moteur IA avant de choisir un prestataire. Ex : « Combien coûte une migration cloud pour une PME ? », « Quelles certifications chercher chez un prestataire AWS ? »',
    usedBy: 'Brand Perception (Brand Referral — recommandations), Veille',
    isEmpty: (s) => !(s.customerQuestions && s.customerQuestions.length > 0),
  },
  {
    key: 'customerObjections',
    title: 'Objections des prospects',
    description:
      'Blocages ou a priori qu\'un prospect exprime au sujet de votre marque en rendez-vous commercial. Ex : « Vous n\'avez pas la certif ISO 27001 », « Je travaille qu\'avec les grands cabinets », « Vous avez mauvaise réputation en ligne ».',
    usedBy: 'Earned Media (validation), Brand Perception (signaux négatifs)',
    isEmpty: (s) => !(s.customerObjections && s.customerObjections.length > 0),
  },
  {
    key: 'avoidTopics',
    title: 'Sujets à éviter',
    description: 'Associations dont la marque ne veut pas (souvent vide — à remplir à la main).',
    usedBy: 'Earned Media (filtrage), Brand Perception',
    isEmpty: (s) => !(s.avoidTopics && s.avoidTopics.length > 0),
  },
  {
    key: 'languagesMaturity',
    title: 'Langues & envergure',
    description: 'Langues cibles pour les audits et envergure géographique actuelle.',
    usedBy: 'Brand Perception, Earned Media',
    isEmpty: (s) => !(s.targetLanguages && s.targetLanguages.length > 0) && !s.envergure,
  },
  {
    key: 'existingAssets',
    title: 'Actifs de notoriété existants',
    description: 'Page Wikipédia, kit presse, mentions presse notables déjà acquises.',
    usedBy: 'Earned Media (sources fiables)',
    isEmpty: (s) =>
      !s.existingAssets?.wikipediaUrl &&
      !s.existingAssets?.pressKitUrl &&
      !(s.existingAssets?.notableMentions && s.existingAssets.notableMentions.length > 0),
  },
];

type WizardStage = 'loading' | 'wizard' | 'done' | 'error';

interface PendingLocation {
  label: string;
  zone: string;
  scope: LocationScope | '';
}

function DiscoveryDialog({
  open,
  brandId,
  brandName,
  onClose,
  onSaved,
}: {
  open: boolean;
  brandId?: string;
  brandName?: string;
  onClose: () => void;
  onSaved?: () => Promise<void> | void;
}) {
  const navigate = useNavigate();
  const { brands } = useBrand();
  const { tenant } = useAuth();
  const tier = tenant?.tier || 'lite';
  const zoneLimit = tenant?.settings?.quotaOverrides?.zones ?? (ZONE_LIMITS[tier] ?? 1);
  const zonesUnlimited = zoneLimit === -1;
  const existingZones = brands.reduce((sum, b) => sum + b.locations.length, 0);

  const [stage, setStage] = useState<WizardStage>('loading');
  const [error, setError] = useState<string | null>(null);
  const [activeStep, setActiveStep] = useState(0);
  const [discovered, setDiscovered] = useState<BrandStrategy>({});
  const [draft, setDraft] = useState<BrandStrategy>({});
  const [pendingLocations, setPendingLocations] = useState<PendingLocation[]>([]);
  const [sectionIndex, setSectionIndex] = useState(0);
  const [saving, setSaving] = useState(false);

  const totalZonesIfAccepted = existingZones + pendingLocations.length;
  const zonesLimitReached = !zonesUnlimited && totalZonesIfAccepted >= zoneLimit;

  // Animate the 4-step loader while /discover runs
  useEffect(() => {
    if (stage !== 'loading') return;
    const interval = setInterval(() => {
      setActiveStep((prev) => Math.min(prev + 1, DISCOVERY_STEPS.length - 1));
    }, 4000);
    return () => clearInterval(interval);
  }, [stage]);

  useEffect(() => {
    if (!open || !brandId) return;
    setStage('loading');
    setError(null);
    setActiveStep(0);
    setDiscovered({});
    setDraft({});
    setPendingLocations([]);
    setSectionIndex(0);

    let cancelled = false;

    api
      .post<{ success: boolean; message?: string; strategy?: BrandStrategy }>(
        `/brands/${brandId}/discover`,
        {},
      )
      .then((res) => {
        if (cancelled) return;
        if (res.success && res.strategy) {
          setDiscovered(res.strategy);
          setDraft(res.strategy);
          setStage('wizard');
        } else {
          setError(res.message || 'Impossible de generer la strategie');
          setStage('error');
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message || 'Erreur de connexion');
        setStage('error');
      });

    return () => {
      cancelled = true;
    };
  }, [open, brandId]);

  const getStepIcon = (idx: number) => {
    if (stage === 'error' && idx === activeStep) {
      return <ErrorIcon color="error" sx={{ fontSize: 20 }} />;
    }
    if (idx < activeStep || stage === 'wizard' || stage === 'done') {
      return <CheckCircleIcon color="success" sx={{ fontSize: 20 }} />;
    }
    if (idx === activeStep) {
      return (
        <AutorenewIcon
          color="primary"
          sx={{
            fontSize: 20,
            animation: 'spin 1s linear infinite',
            '@keyframes spin': {
              '0%': { transform: 'rotate(0deg)' },
              '100%': { transform: 'rotate(360deg)' },
            },
          }}
        />
      );
    }
    return <RadioButtonUncheckedIcon sx={{ fontSize: 20, color: 'text.disabled' }} />;
  };

  // Build the accepted strategy from the draft (sections already kept are in draft)
  const currentSection = SECTIONS[sectionIndex];

  const handleAccept = () => {
    // draft already contains the user's current edits — just advance
    if (sectionIndex + 1 < SECTIONS.length) {
      setSectionIndex(sectionIndex + 1);
    } else {
      handleFinish(draft);
    }
  };

  const handleSkip = () => {
    // "Passer" = do not modify this section (keep scraped values untouched).
    // Advance without clearing the draft.
    if (sectionIndex + 1 < SECTIONS.length) {
      setSectionIndex(sectionIndex + 1);
    } else {
      handleFinish(draft);
    }
  };

  const handleBack = () => {
    if (sectionIndex > 0) {
      setSectionIndex(sectionIndex - 1);
    }
  };

  const handleFinish = async (finalStrategy: BrandStrategy) => {
    if (!brandId) return;
    setSaving(true);
    try {
      // Send only non-empty fields to avoid clearing existing ones on the server
      const payload = pruneEmpty(finalStrategy);
      await api.patch(`/brands/${brandId}/strategy`, payload);

      // Create any pending locations from the wizard
      for (const loc of pendingLocations) {
        try {
          await api.post(`/brands/${brandId}/locations`, {
            label: loc.label,
            zone: loc.zone || loc.label,
            ...(loc.scope && { scope: loc.scope }),
          });
        } catch {
          // Non-fatal: strategy was saved, continue and surface via onSaved refresh
        }
      }

      if (onSaved) await onSaved();
      setStage('done');
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la sauvegarde');
      setStage('error');
    } finally {
      setSaving(false);
    }
  };

  const handleViewStrategy = () => {
    onClose();
    navigate('/strategy');
  };

  const renderLoading = () => (
    <>
      <DialogContent>
        <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {DISCOVERY_STEPS.map((label, idx) => (
            <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {getStepIcon(idx)}
              <Typography
                variant="body2"
                color={idx <= activeStep ? 'text.primary' : 'text.disabled'}
                fontWeight={idx === activeStep ? 600 : 400}
              >
                {label}
              </Typography>
            </Box>
          ))}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fermer (analyse en cours...)</Button>
      </DialogActions>
    </>
  );

  const renderWizard = () => (
    <>
      <DialogContent sx={{ pt: '16px !important' }}>
        <Typography variant="caption" color="text.secondary">
          Étape {sectionIndex + 1} / {SECTIONS.length}
        </Typography>
        <Typography variant="h6" fontWeight={600} sx={{ mt: 0.5 }}>
          {currentSection.title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
          {currentSection.description}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          Utilisé par : {currentSection.usedBy}
        </Typography>

        {currentSection.key === 'locations' ? (
          <>
            <LocationWizardEditor
              locations={pendingLocations}
              setLocations={setPendingLocations}
              suggestedLabel={draft.geographicFocus}
              zoneLimit={zoneLimit}
              zonesUnlimited={zonesUnlimited}
              totalIfAccepted={totalZonesIfAccepted}
              zonesLimitReached={zonesLimitReached}
              tierLabel={tier}
            />
            <Alert severity="info" icon={false} sx={{ mt: 2 }}>
              Les localisations seront créées à la fin du wizard. Vous pourrez en ajouter ou
              modifier depuis <strong>Brand Knowledge</strong>.
            </Alert>
          </>
        ) : (
          <>
            {currentSection.isEmpty(discovered) && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Aucune suggestion n'a pu être extraite du site. Vous pouvez compléter ci-dessous ou
                passer cette étape.
              </Alert>
            )}

            <SectionEditor sectionKey={currentSection.key} draft={draft} setDraft={setDraft} />

            <Alert severity="info" icon={false} sx={{ mt: 2 }}>
              Vous pourrez modifier ces informations plus tard dans <strong>Brand Knowledge</strong>.
            </Alert>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', px: 3 }}>
        <Button onClick={handleBack} disabled={sectionIndex === 0 || saving}>
          Précédent
        </Button>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={handleSkip} disabled={saving}>
            Passer
          </Button>
          <Button variant="contained" onClick={handleAccept} disabled={saving}>
            {sectionIndex + 1 === SECTIONS.length ? 'Terminer' : 'Accepter et continuer'}
          </Button>
        </Box>
      </DialogActions>
    </>
  );

  const renderDone = () => (
    <>
      <DialogContent>
        <Alert severity="success" sx={{ mb: 2 }}>
          Stratégie enregistrée. Vous pouvez la modifier à tout moment dans Brand Knowledge.
        </Alert>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fermer</Button>
        <Button variant="contained" onClick={handleViewStrategy}>
          Voir Brand Knowledge
        </Button>
      </DialogActions>
    </>
  );

  const renderError = () => (
    <>
      <DialogContent>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || 'Une erreur est survenue'}
        </Alert>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={onClose}>
          Fermer
        </Button>
      </DialogActions>
    </>
  );

  return (
    <Dialog open={open} maxWidth="sm" fullWidth>
      <DialogTitle>
        {stage === 'loading' && `Analyse de ${brandName || 'la marque'}`}
        {stage === 'wizard' && `Stratégie suggérée — ${brandName || ''}`}
        {stage === 'done' && 'Stratégie enregistrée'}
        {stage === 'error' && 'Erreur'}
      </DialogTitle>
      {stage === 'loading' && renderLoading()}
      {stage === 'wizard' && renderWizard()}
      {stage === 'done' && renderDone()}
      {stage === 'error' && renderError()}
    </Dialog>
  );
}

// Strip empty strings / empty arrays so we don't overwrite existing brand data with nothing
function pruneEmpty(s: BrandStrategy): Partial<BrandStrategy> {
  const out: Partial<BrandStrategy> = {};
  if (s.industry) out.industry = s.industry;
  if (s.aliases && s.aliases.length > 0) out.aliases = s.aliases;
  if (s.clientPersonas) out.clientPersonas = s.clientPersonas;
  if (s.businessModel) out.businessModel = s.businessModel;
  if (s.geographicFocus) out.geographicFocus = s.geographicFocus;
  if (s.description) out.description = s.description;
  if (s.differentiators && s.differentiators.length > 0) out.differentiators = s.differentiators;
  if (s.scenarios && s.scenarios.length > 0) out.scenarios = s.scenarios;
  if (s.keywords && s.keywords.length > 0) out.keywords = s.keywords;
  if (s.customerQuestions && s.customerQuestions.length > 0) out.customerQuestions = s.customerQuestions;
  if (s.customerObjections && s.customerObjections.length > 0) out.customerObjections = s.customerObjections;
  if (s.avoidTopics && s.avoidTopics.length > 0) out.avoidTopics = s.avoidTopics;
  if (s.targetLanguages && s.targetLanguages.length > 0) out.targetLanguages = s.targetLanguages;
  if (s.envergure) out.envergure = s.envergure;
  if (s.existingAssets) {
    const a = s.existingAssets;
    const trimmed: BrandStrategy['existingAssets'] = {};
    if (a.wikipediaUrl) trimmed!.wikipediaUrl = a.wikipediaUrl;
    if (a.pressKitUrl) trimmed!.pressKitUrl = a.pressKitUrl;
    if (a.notableMentions && a.notableMentions.length > 0) trimmed!.notableMentions = a.notableMentions;
    if (Object.keys(trimmed!).length > 0) out.existingAssets = trimmed;
  }
  return out;
}

function SectionEditor({
  sectionKey,
  draft,
  setDraft,
}: {
  sectionKey: SectionKey;
  draft: BrandStrategy;
  setDraft: React.Dispatch<React.SetStateAction<BrandStrategy>>;
}) {
  switch (sectionKey) {
    case 'identity':
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Secteur d'activité"
            size="small"
            fullWidth
            value={draft.industry || ''}
            onChange={(e) =>
              setDraft({ ...draft, industry: e.target.value.slice(0, BRAND_STRATEGY_LIMITS.industry) })
            }
            placeholder="Ex: SaaS, Conseil, E-commerce..."
            inputProps={{ maxLength: BRAND_STRATEGY_LIMITS.industry }}
            helperText={`${(draft.industry || '').length} / ${BRAND_STRATEGY_LIMITS.industry}`}
          />
          <ChipEditor
            label="Aliases / noms alternatifs"
            items={draft.aliases || []}
            onChange={(items) => setDraft({ ...draft, aliases: items })}
            placeholder="Entrée pour ajouter"
            limits={BRAND_STRATEGY_LIMITS.aliases}
          />
        </Box>
      );
    case 'market':
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Personas clients"
            size="small"
            fullWidth
            multiline
            minRows={3}
            value={draft.clientPersonas || ''}
            onChange={(e) =>
              setDraft({
                ...draft,
                clientPersonas: e.target.value.slice(0, BRAND_STRATEGY_LIMITS.clientPersonas),
              })
            }
            inputProps={{ maxLength: BRAND_STRATEGY_LIMITS.clientPersonas }}
            helperText={`${(draft.clientPersonas || '').length} / ${BRAND_STRATEGY_LIMITS.clientPersonas}`}
          />
          <Select
            size="small"
            fullWidth
            value={draft.businessModel || ''}
            onChange={(e) =>
              setDraft({ ...draft, businessModel: (e.target.value as any) || undefined })
            }
            displayEmpty
          >
            <MenuItem value="">Modèle business — non défini</MenuItem>
            <MenuItem value="b2b">B2B</MenuItem>
            <MenuItem value="b2c">B2C</MenuItem>
            <MenuItem value="both">B2B & B2C</MenuItem>
          </Select>
          <TextField
            label="Focus géographique"
            size="small"
            fullWidth
            value={draft.geographicFocus || ''}
            onChange={(e) =>
              setDraft({
                ...draft,
                geographicFocus: e.target.value.slice(0, BRAND_STRATEGY_LIMITS.geographicFocus),
              })
            }
            inputProps={{ maxLength: BRAND_STRATEGY_LIMITS.geographicFocus }}
            helperText={`${(draft.geographicFocus || '').length} / ${BRAND_STRATEGY_LIMITS.geographicFocus}`}
          />
        </Box>
      );
    case 'valueProp':
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Description"
            size="small"
            fullWidth
            multiline
            minRows={3}
            value={draft.description || ''}
            onChange={(e) =>
              setDraft({ ...draft, description: e.target.value.slice(0, BRAND_STRATEGY_LIMITS.description) })
            }
            inputProps={{ maxLength: BRAND_STRATEGY_LIMITS.description }}
            helperText={`${(draft.description || '').length} / ${BRAND_STRATEGY_LIMITS.description}`}
          />
          <ChipEditor
            label="Différenciateurs clés"
            items={draft.differentiators || []}
            onChange={(items) => setDraft({ ...draft, differentiators: items })}
            placeholder="Entrée pour ajouter"
            limits={BRAND_STRATEGY_LIMITS.differentiators}
          />
        </Box>
      );
    case 'scenarios':
      return (
        <ChipEditor
          label="Scénarios à surveiller"
          items={draft.scenarios || []}
          onChange={(items) => setDraft({ ...draft, scenarios: items })}
          placeholder="Ex: Migration cloud pour PME — Entrée pour ajouter"
          limits={BRAND_STRATEGY_LIMITS.scenarios}
        />
      );
    case 'keywords':
      return (
        <ChipEditor
          label="Mots-clés et thèmes"
          items={draft.keywords || []}
          onChange={(items) => setDraft({ ...draft, keywords: items })}
          placeholder="Ex: GEO, optimisation IA — Entrée pour ajouter"
          limits={BRAND_STRATEGY_LIMITS.keywords}
        />
      );
    case 'customerQuestions':
      return (
        <ChipEditor
          label="Questions avant achat"
          items={draft.customerQuestions || []}
          onChange={(items) => setDraft({ ...draft, customerQuestions: items })}
          placeholder="Ex: Combien coûte... ? Quels délais ? — Entrée pour ajouter"
          limits={BRAND_STRATEGY_LIMITS.customerQuestions}
        />
      );
    case 'customerObjections':
      return (
        <ChipEditor
          label="Objections des prospects"
          items={draft.customerObjections || []}
          onChange={(items) => setDraft({ ...draft, customerObjections: items })}
          placeholder="Ex: Pas de certif ISO, mauvaise réputation... — Entrée pour ajouter"
          limits={BRAND_STRATEGY_LIMITS.customerObjections}
        />
      );
    case 'avoidTopics':
      return (
        <ChipEditor
          label="Sujets à éviter"
          items={draft.avoidTopics || []}
          onChange={(items) => setDraft({ ...draft, avoidTopics: items })}
          placeholder="Entrée pour ajouter"
          limits={BRAND_STRATEGY_LIMITS.avoidTopics}
        />
      );
    case 'languagesMaturity':
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <ChipEditor
            label="Langues cibles (codes ISO)"
            items={draft.targetLanguages || []}
            onChange={(items) => setDraft({ ...draft, targetLanguages: items })}
            placeholder="Ex: fr, en, de — Entrée pour ajouter"
            limits={BRAND_STRATEGY_LIMITS.targetLanguages}
          />
          <Select
            size="small"
            fullWidth
            value={draft.envergure || ''}
            onChange={(e) =>
              setDraft({ ...draft, envergure: (e.target.value as Envergure) || undefined })
            }
            displayEmpty
          >
            <MenuItem value="">Envergure — non définie</MenuItem>
            {Object.entries(ENVERGURE_LABELS).map(([val, label]) => (
              <MenuItem key={val} value={val}>
                {label}
              </MenuItem>
            ))}
          </Select>
          <Typography variant="caption" color="text.secondary">
            Empreinte géographique actuelle (pas l'ambition).
          </Typography>
        </Box>
      );
    case 'existingAssets':
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Page Wikipédia (URL)"
            size="small"
            fullWidth
            value={draft.existingAssets?.wikipediaUrl || ''}
            onChange={(e) =>
              setDraft({
                ...draft,
                existingAssets: {
                  ...draft.existingAssets,
                  wikipediaUrl: e.target.value.slice(0, BRAND_STRATEGY_LIMITS.existingAssets.wikipediaUrl),
                },
              })
            }
            inputProps={{ maxLength: BRAND_STRATEGY_LIMITS.existingAssets.wikipediaUrl }}
          />
          <TextField
            label="Kit presse (URL)"
            size="small"
            fullWidth
            value={draft.existingAssets?.pressKitUrl || ''}
            onChange={(e) =>
              setDraft({
                ...draft,
                existingAssets: {
                  ...draft.existingAssets,
                  pressKitUrl: e.target.value.slice(0, BRAND_STRATEGY_LIMITS.existingAssets.pressKitUrl),
                },
              })
            }
            inputProps={{ maxLength: BRAND_STRATEGY_LIMITS.existingAssets.pressKitUrl }}
          />
          <ChipEditor
            label="Mentions presse notables"
            items={draft.existingAssets?.notableMentions || []}
            onChange={(items) =>
              setDraft({
                ...draft,
                existingAssets: { ...draft.existingAssets, notableMentions: items },
              })
            }
            placeholder="URL ou nom de source — Entrée pour ajouter"
            limits={BRAND_STRATEGY_LIMITS.existingAssets.notableMentions}
          />
        </Box>
      );
  }
}

function LocationWizardEditor({
  locations,
  setLocations,
  suggestedLabel,
  zoneLimit,
  zonesUnlimited,
  totalIfAccepted,
  zonesLimitReached,
  tierLabel,
}: {
  locations: PendingLocation[];
  setLocations: React.Dispatch<React.SetStateAction<PendingLocation[]>>;
  suggestedLabel?: string;
  zoneLimit: number;
  zonesUnlimited: boolean;
  totalIfAccepted: number;
  zonesLimitReached: boolean;
  tierLabel: string;
}) {
  const [label, setLabel] = useState('');
  const [zone, setZone] = useState('');
  const [scope, setScope] = useState<LocationScope | ''>('');

  const tierName = tierLabel.charAt(0).toUpperCase() + tierLabel.slice(1);

  const handleAdd = () => {
    const trimmed = label.trim();
    if (!trimmed) return;
    setLocations((prev) => [...prev, { label: trimmed, zone: zone.trim(), scope }]);
    setLabel('');
    setZone('');
    setScope('');
  };

  const handleUseSuggestion = () => {
    if (suggestedLabel && !label) {
      setLabel(suggestedLabel);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="caption" color="text.secondary">
          Quota du plan {tierName}
        </Typography>
        <Chip
          size="small"
          label={
            zonesUnlimited
              ? `${totalIfAccepted} — illimité`
              : `${totalIfAccepted} / ${zoneLimit}`
          }
          color={zonesLimitReached ? 'warning' : 'default'}
          variant="outlined"
        />
      </Box>

      {locations.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {locations.map((loc, i) => (
            <Chip
              key={`${loc.label}-${i}`}
              label={`${loc.label}${loc.scope ? ` (${SCOPE_LABELS[loc.scope]})` : ''}`}
              size="small"
              onDelete={() => setLocations((prev) => prev.filter((_, idx) => idx !== i))}
            />
          ))}
        </Box>
      )}

      {suggestedLabel && locations.length === 0 && !label && (
        <Button size="small" variant="text" onClick={handleUseSuggestion} sx={{ alignSelf: 'flex-start' }}>
          Utiliser « {suggestedLabel} » comme localisation
        </Button>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <TextField
          label="Libellé"
          size="small"
          fullWidth
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Ex: Paris, Siège France..."
          disabled={zonesLimitReached}
        />
        <TextField
          label="Zone géographique"
          size="small"
          fullWidth
          value={zone}
          onChange={(e) => setZone(e.target.value)}
          placeholder="Si vide, le libellé sera utilisé"
          disabled={zonesLimitReached}
        />
        <Select
          size="small"
          fullWidth
          value={scope}
          onChange={(e) => setScope(e.target.value as LocationScope | '')}
          displayEmpty
          disabled={zonesLimitReached}
        >
          <MenuItem value="">Portée (optionnel)</MenuItem>
          {Object.entries(SCOPE_LABELS).map(([val, lbl]) => (
            <MenuItem key={val} value={val}>
              {lbl}
            </MenuItem>
          ))}
        </Select>
        <Button
          variant="outlined"
          onClick={handleAdd}
          disabled={!label.trim() || zonesLimitReached}
          startIcon={<AddIcon />}
          sx={{ alignSelf: 'flex-start' }}
        >
          Ajouter à la liste
        </Button>
        {zonesLimitReached && (
          <Alert severity="warning">
            Limite du plan {tierName} atteinte. Passez au plan supérieur pour ajouter plus de zones.
          </Alert>
        )}
      </Box>
    </Box>
  );
}

function ChipEditor({
  label,
  items,
  onChange,
  placeholder,
  limits,
}: {
  label: string;
  items: string[];
  onChange: (items: string[]) => void;
  placeholder?: string;
  limits?: { maxItems: number; maxCharsPerItem: number };
}) {
  const [input, setInput] = useState('');
  const atCapacity = limits ? items.length >= limits.maxItems : false;
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        {label}
      </Typography>
      {items.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
          {items.map((item, i) => (
            <Chip
              key={`${item}-${i}`}
              label={item}
              size="small"
              onDelete={() => onChange(items.filter((_, idx) => idx !== i))}
              sx={{
                maxWidth: '100%',
                height: 'auto',
                py: 0.5,
                '& .MuiChip-label': {
                  whiteSpace: 'normal',
                  overflow: 'visible',
                  textOverflow: 'clip',
                  lineHeight: 1.3,
                },
              }}
            />
          ))}
        </Box>
      )}
      <TextField
        fullWidth
        size="small"
        value={input}
        onChange={(e) =>
          setInput(limits ? e.target.value.slice(0, limits.maxCharsPerItem) : e.target.value)
        }
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            const trimmed = input.trim();
            if (limits && (items.length >= limits.maxItems || trimmed.length > limits.maxCharsPerItem)) {
              setInput('');
              return;
            }
            if (trimmed && !items.includes(trimmed)) {
              onChange([...items, trimmed]);
            }
            setInput('');
          }
        }}
        placeholder={placeholder}
        disabled={atCapacity}
        inputProps={limits ? { maxLength: limits.maxCharsPerItem } : undefined}
        helperText={
          limits ? `${items.length} / ${limits.maxItems} items · max ${limits.maxCharsPerItem} car.` : undefined
        }
      />
    </Box>
  );
}

// ── Email Recipients Dialog ──

function EmailRecipientsDialog({
  open,
  brandId,
  recipients: initialRecipients,
  onClose,
  onSaved,
}: {
  open: boolean;
  brandId?: string;
  recipients: EmailRecipient[];
  onClose: () => void;
  onSaved: (brandId: string, recipients: EmailRecipient[]) => void;
}) {
  const [recipients, setRecipients] = useState<EmailRecipient[]>([]);
  const [newEmail, setNewEmail] = useState('');
  const [newLanguage, setNewLanguage] = useState<EmailLanguage>(DEFAULT_EMAIL_LANGUAGE);
  const [newFrequency, setNewFrequency] = useState<'daily' | 'weekly' | 'monthly'>('weekly');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setRecipients([...initialRecipients]);
      setNewEmail('');
      setNewLanguage(DEFAULT_EMAIL_LANGUAGE);
      setNewFrequency('weekly');
      setError(null);
    }
  }, [open, initialRecipients]);

  const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleAdd = () => {
    const trimmed = newEmail.trim().toLowerCase();
    if (!trimmed) return;
    if (!isValidEmail(trimmed)) {
      setError('Adresse email invalide');
      return;
    }
    if (recipients.some((r) => r.email === trimmed)) {
      setError('Cette adresse est deja dans la liste');
      return;
    }
    setRecipients([...recipients, { email: trimmed, language: newLanguage, frequency: newFrequency }]);
    setNewEmail('');
    setError(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAdd();
    }
  };

  const handleRemove = (email: string) => {
    setRecipients(recipients.filter((r) => r.email !== email));
  };

  const handleChangeLanguage = (email: string, language: EmailLanguage) => {
    setRecipients(recipients.map((r) => r.email === email ? { ...r, language } : r));
  };

  const handleChangeFrequency = (
    email: string,
    frequency: 'daily' | 'weekly' | 'monthly',
  ) => {
    setRecipients(recipients.map((r) => r.email === email ? { ...r, frequency } : r));
  };

  const handleSave = () => {
    if (brandId) {
      onSaved(brandId, recipients);
    }
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Destinataires du rapport</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
        {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
        <Typography variant="body2" color="text.secondary">
          Ces adresses recevront le rapport d'analyse periodique pour cette marque, chacune dans la langue choisie.
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField
            label="Adresse email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            onKeyDown={handleKeyDown}
            fullWidth
            size="small"
            placeholder="nom@exemple.com"
          />
          <Select
            size="small"
            value={newLanguage}
            onChange={(e) => setNewLanguage(e.target.value as EmailLanguage)}
            sx={{ minWidth: 120 }}
          >
            {EMAIL_LANGUAGES.map((l) => (
              <MenuItem key={l.code} value={l.code}>{l.label}</MenuItem>
            ))}
          </Select>
          <Select
            size="small"
            value={newFrequency}
            onChange={(e) => setNewFrequency(e.target.value as 'daily' | 'weekly' | 'monthly')}
            sx={{ minWidth: 110 }}
          >
            <MenuItem value="daily">Quotidien</MenuItem>
            <MenuItem value="weekly">Hebdo</MenuItem>
            <MenuItem value="monthly">Mensuel</MenuItem>
          </Select>
          <Button variant="outlined" onClick={handleAdd} disabled={!newEmail.trim()}>
            Ajouter
          </Button>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          {recipients.map((r) => (
            <Box key={r.email} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Chip
                label={r.email}
                size="small"
                onDelete={() => handleRemove(r.email)}
                sx={{ flexGrow: 1, justifyContent: 'space-between' }}
              />
              <Select
                size="small"
                value={r.language}
                onChange={(e) => handleChangeLanguage(r.email, e.target.value as EmailLanguage)}
                sx={{ minWidth: 120 }}
              >
                {EMAIL_LANGUAGES.map((l) => (
                  <MenuItem key={l.code} value={l.code}>{l.label}</MenuItem>
                ))}
              </Select>
              <Select
                size="small"
                value={r.frequency || 'weekly'}
                onChange={(e) =>
                  handleChangeFrequency(r.email, e.target.value as 'daily' | 'weekly' | 'monthly')
                }
                sx={{ minWidth: 110 }}
              >
                <MenuItem value="daily">Quotidien</MenuItem>
                <MenuItem value="weekly">Hebdo</MenuItem>
                <MenuItem value="monthly">Mensuel</MenuItem>
              </Select>
            </Box>
          ))}
          {recipients.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              Aucun destinataire
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annuler</Button>
        <Button variant="contained" onClick={handleSave}>
          Enregistrer
        </Button>
      </DialogActions>
    </Dialog>
  );
}
